import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, of, timer } from 'rxjs';
import { catchError, filter, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { ReceivingLogApiService } from '../../domain/receiving-log/api/receiving-log-api.service';
import { ReceivingLogStatusResponse } from '../../models/logs/ReceivingLogStatusResponse';
import { JobType, SubmitJob } from '../../models/logs/SubmitJob';

const POLL_INTERVAL_MS = 4000;
/** Hard cap so a stuck (never-terminal) log can't leave a zombie poll running forever. */
const MAX_POLLS = 150;

/** Display context for a tracked submission, so a global toast can name the PO. */
export interface SubmitTrackContext {
  poNumber?: string;
}

/** Emitted when a tracked submission resolves to NeedsAttention (a job permanently failed). */
export interface SubmitFailureEvent {
  receivingLogId: number;
  poNumber?: string;
  /** The permanently-failed jobs (with `lastError`/attempts), for retry + the IT email body. */
  failedJobs: SubmitJob[];
  /** Every job on the log — lets the retry decide whether an email needs re-sending with photos. */
  allJobs: SubmitJob[];
}

const PHOTO_JOB_TYPES: JobType[] = ['ShipmentPhotos', 'DamagePhotos', 'SlipPhotos'];
const EMAIL_JOB_TYPES: JobType[] = ['NotificationEmail', 'ExpeditingEmail'];

/**
 * Decides the `/retry` payload for a set of failed jobs. When a photo job is being
 * retried and the log had an email that was meant to include photos but already went out
 * without them (`includesPhotos === true`, and it succeeded so it isn't already in the
 * retry set), that email is added and `force` is set — so the backend re-sends it now
 * that the photos have uploaded (it stamps a header note on the re-send). Emails that
 * don't include photos (`includesPhotos === false`) are skipped, since re-sending would
 * just be a duplicate with no new content. With no photo job in the retry, only the
 * failed jobs are re-enqueued (no force).
 */
export function buildRetryRequest(
  failedJobs: SubmitJob[],
  allJobs: SubmitJob[]
): { jobTypes: JobType[]; force: boolean } {
  const types = new Set<JobType>(failedJobs.map((job) => job.type));
  const retryingPhotos = failedJobs.some((job) => PHOTO_JOB_TYPES.includes(job.type));
  let force = false;
  if (retryingPhotos) {
    for (const job of allJobs) {
      // Re-send only emails that were supposed to carry photos but sent without them
      // (succeeded, so not already in the retry set). Forcing re-runs the succeeded job.
      if (EMAIL_JOB_TYPES.includes(job.type) && job.includesPhotos === true && !types.has(job.type)) {
        types.add(job.type);
        force = true;
      }
    }
  }
  return { jobTypes: [...types], force };
}

/**
 * Resolves the final state of an async ("Accepted") submit by polling `…/status`
 * until the backend's background jobs settle to `Complete` or `NeedsAttention`.
 *
 * The backend durably stages the submitted photo bytes itself and keeps failed ones
 * until resolved, so the frontend holds nothing and a re-enqueue needs no files — this
 * service is pure polling + a manual re-enqueue trigger.
 *
 * `providedIn: 'root'` and self-subscribing so tracking survives the review-step
 * component being destroyed on navigation to the Done screen. State is exposed per log
 * id via `statusFor(logId)` so any screen can render the live status.
 */
@Injectable({ providedIn: 'root' })
export class SubmitStatusService {
  private readonly _statuses = new Map<number, BehaviorSubject<ReceivingLogStatusResponse | null>>();
  private readonly _subs = new Map<number, Subscription>();
  private readonly _contexts = new Map<number, SubmitTrackContext>();
  private readonly _needsAttention = new Subject<SubmitFailureEvent>();
  private readonly _completed = new Subject<number>();

  constructor(private _api: ReceivingLogApiService) {}

  /**
   * Fires when a tracked submission resolves to NeedsAttention. A global listener
   * (SubmitFailureNotifierService) toasts these so a failure is surfaced wherever the
   * user is — it's the single failure UI (the Done screen only shows progress/success).
   */
  public readonly needsAttention$: Observable<SubmitFailureEvent> = this._needsAttention.asObservable();

  /**
   * Emits a log id whenever its background work resolves to `Complete`. Lets a global
   * listener confirm a user-triggered retry actually landed (the normal happy path also
   * fires this, so listeners scope it to logs the user retried).
   */
  public readonly completed$: Observable<number> = this._completed.asObservable();

  /** The live status for a log id (null until the first poll resolves). */
  public statusFor(receivingLogId: number): Observable<ReceivingLogStatusResponse | null> {
    return this._subject(receivingLogId).asObservable();
  }

  /** Latest known status for a log id, if any. */
  public snapshot(receivingLogId: number): ReceivingLogStatusResponse | null {
    return this._statuses.get(receivingLogId)?.getValue() ?? null;
  }

  /**
   * Starts (or resumes) polling the log's status until it resolves to `Complete` or
   * `NeedsAttention`. Idempotent while a poll is active; a finished poll can be
   * restarted (e.g. after a re-enqueue). Transient errors are swallowed so a blip
   * doesn't stop the loop.
   */
  public track(receivingLogId: number, context?: SubmitTrackContext): void {
    if (context) { this._contexts.set(receivingLogId, context); }
    if (this._subs.has(receivingLogId)) { return; }

    const subject = this._subject(receivingLogId);
    const sub = timer(0, POLL_INTERVAL_MS)
      .pipe(
        take(MAX_POLLS),
        switchMap(() =>
          this._api.getReceivingLogStatus(receivingLogId).pipe(catchError(() => of(null)))
        ),
        filter((status): status is ReceivingLogStatusResponse => status !== null),
        tap((status) => {
          subject.next(status);
          if (status.outcome === 'NeedsAttention') {
            this._emitNeedsAttention(receivingLogId, status);
          } else if (status.outcome === 'Complete') {
            this._completed.next(receivingLogId);
          }
        }),
        takeWhile((status) => status.outcome === 'Pending', true)
      )
      .subscribe({
        complete: () => this._subs.delete(receivingLogId),
        error: () => this._subs.delete(receivingLogId),
      });
    this._subs.set(receivingLogId, sub);
  }

  /** Raises a global failure event (toasted app-wide by SubmitFailureNotifierService). */
  private _emitNeedsAttention(receivingLogId: number, status: ReceivingLogStatusResponse): void {
    this._needsAttention.next({
      receivingLogId,
      poNumber: this._contexts.get(receivingLogId)?.poNumber,
      failedJobs: status.jobs.filter((job) => job.isPermanentlyFailed),
      allJobs: status.jobs,
    });
  }

  /**
   * Re-enqueues failed background jobs for a log. The worker re-uploads any failed
   * photo job from the server-staged bytes, so no files are sent. Resumes polling on
   * the response.
   *
   * @param jobTypes The job types to retry (omit/empty to retry all failed).
   * @param force Re-run the named jobs even if they already succeeded (email re-send).
   */
  public retry(
    receivingLogId: number,
    jobTypes?: JobType[],
    force: boolean = false
  ): Observable<ReceivingLogStatusResponse> {
    return this._api.retryReceivingLogJobs(receivingLogId, jobTypes, force).pipe(
      tap((status) => {
        this._subject(receivingLogId).next(status);
        // Poll may have stopped at NeedsAttention; restart it for the reset jobs.
        this.track(receivingLogId);
      })
    );
  }

  private _subject(receivingLogId: number): BehaviorSubject<ReceivingLogStatusResponse | null> {
    let subject = this._statuses.get(receivingLogId);
    if (!subject) {
      subject = new BehaviorSubject<ReceivingLogStatusResponse | null>(null);
      this._statuses.set(receivingLogId, subject);
    }
    return subject;
  }
}
